import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '@clerk/clerk-react';
import { colors } from '../theme';

const API_BASE = process.env.REACT_APP_API_URL || '';

const containerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  flexWrap: 'wrap'
};

const secondaryButtonStyle = {
  backgroundColor: 'transparent',
  color: colors.accent,
  padding: '0.5rem 1rem',
  borderRadius: '6px',
  border: `1px solid ${colors.accent}`,
  fontWeight: 600,
  cursor: 'pointer'
};

function buildPdfApiPath(rmaId, locale, download = false) {
  if (!rmaId) return null;
  const trimmedBase = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
  const url = `${trimmedBase}/rma/${encodeURIComponent(rmaId)}/pdf?locale=${locale}`;
  return download ? `${url}&download=true` : url;
}

export default function RmaPdfActions({ rmaId, locale, onLocaleChange }) {
  const { getToken } = useAuth();
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!rmaId) return null;

  async function fetchPdfBlob(download) {
    const path = buildPdfApiPath(rmaId, locale, download);
    if (!path) return null;
    const token = await getToken();
    const res = await fetch(path, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!res.ok) {
      let msg = `PDF request failed (${res.status})`;
      try {
        const body = await res.json();
        if (body?.message) msg = body.message;
      } catch (_) {}
      throw new Error(msg);
    }
    return await res.blob();
  }

  const handleDownload = async () => {
    if (downloadLoading) return;
    setDownloadLoading(true);
    setError(null);
    try {
      const blob = await fetchPdfBlob(true);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `RMA-${rmaId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      setError(err.message || 'Failed to download PDF');
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', alignItems: 'flex-end' }}>
      <div style={containerStyle}>
        <button
          onClick={handleDownload}
          disabled={downloadLoading}
          style={{ ...secondaryButtonStyle, opacity: downloadLoading ? 0.65 : 1 }}
        >
          {downloadLoading ? 'Downloading…' : 'Download PDF'}
        </button>
      </div>
      {error && (
        <span style={{ color: '#EC2124', fontSize: '0.78rem' }}>{error}</span>
      )}
    </div>
  );
}

RmaPdfActions.propTypes = {
  rmaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  locale: PropTypes.string,
  onLocaleChange: PropTypes.func
};
